import { useSettingsContext } from '@eversports/react-app-base/SettingsContext'

import { createIcsUrl } from '../helpers/create-ics-url'
import { useIntl } from '../localization/react'
import { EventBookableItem, TimeRange } from '../typings/BookableItem'
import Location from '../typings/Location'

const hasField = (field: string | undefined | null) => field && field.trim().length > 0

const formatLocation = (loc: Location) => {
  const { street, detail: doorNumber, zip, city } = loc
  const streetAndNumber = hasField(doorNumber) ? `${street} ${doorNumber}` : street
  const zipAndCity = hasField(zip) && hasField(city) ? `, ${zip} ${city}` : ''
  return streetAndNumber.concat(zipAndCity)
}

interface UseCalendarExportResult {
  exportToCalendar: (bookableItem: EventBookableItem, eventSessions: Array<TimeRange>) => void
}

export const useCalendarExport = (): UseCalendarExportResult => {
  const intl = useIntl()
  const { language, host } = useSettingsContext()

  const exportToCalendar = (bookableItem: EventBookableItem, eventSessions: Array<TimeRange>) => {
    const { activityName, location: bookingLocation, venue: bookingVenue, id: activityId, description } = bookableItem
    const hasOnlineStream = bookableItem.eventSessions[0]?.hasOnlineStream ?? false
    const room = bookableItem.eventSessions[0]?.court?.name
    const trainer = bookableItem.eventSessions[0]?.trainer?.nickname

    const baseUrl = host || window.location.origin
    const activityUrl = `${baseUrl}/activity/${activityId}`
    const address = hasOnlineStream
      ? intl.onlineLocation()
      : bookingLocation
        ? formatLocation(bookingLocation)
        : bookingVenue.name

    const events = eventSessions.map((session) => {
      const descriptionParts: Array<string> = []

      descriptionParts.push(bookingVenue.name)

      if (trainer) {
        descriptionParts.push(intl.activityDetailsCalendarTrainer({ trainer }))
      }

      if (room) {
        descriptionParts.push(intl.activityDetailsCalendarRoom({ room }))
      }

      descriptionParts.push(intl.activityDetailsCalendarViewInfo({ activityUrl }))

      if (description && description.plainText) {
        descriptionParts.push('\n')
        descriptionParts.push(description.plainText)
      }

      const eventDescription = descriptionParts.join('\n')

      return {
        summary: activityName,
        start: session.start,
        end: session.end,
        address,
        description: eventDescription,
      }
    })

    const icsUrl = createIcsUrl({ events })

    const startDate = new Date(eventSessions[0].start)
    const localShortDate = startDate.toLocaleDateString(language, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })

    const fileName = `${activityName}_${localShortDate}`
    const link = document.createElement('a')
    link.download = fileName
    link.href = `data:text/calendar;charset=utf8,${encodeURIComponent(icsUrl)}`
    link.click()
    link.remove()
  }

  return { exportToCalendar }
}
